'use client';

import {
    ClientAiQuotaFeatureAvailabilitySummaryEntity,
    ClientAiQuotaSummaryEntity,
} from '@/entities/client-ai-quota.entity';
import { ClientFeatureControlsEntity } from '@/entities/client-feature-controls.entity';
import { createDefaultClientFeatureControls, normalizeClientFeatureControlsEntity } from '@/shared/client-settings';
import {
    createDefaultClientAiQuotaSummary,
    normalizeClientAiQuotaSummaryEntity,
    resolveClientAiQuotaFeatureAvailability,
} from '@/shared/client-ai-quota';
import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type ClientFeatureControlsContextType = {
    featureControls: ClientFeatureControlsEntity;
    aiQuota: ClientAiQuotaSummaryEntity;
    featureAvailability: ClientAiQuotaFeatureAvailabilitySummaryEntity;
    setFeatureControls: Dispatch<SetStateAction<ClientFeatureControlsEntity>>;
    setAiQuota: Dispatch<SetStateAction<ClientAiQuotaSummaryEntity>>;
};

const DEFAULT_CLIENT_FEATURE_CONTROLS = createDefaultClientFeatureControls();
const DEFAULT_CLIENT_AI_QUOTA = createDefaultClientAiQuotaSummary();
const DEFAULT_FEATURE_AVAILABILITY = resolveClientAiQuotaFeatureAvailability({
    featureControls: DEFAULT_CLIENT_FEATURE_CONTROLS,
    quota: DEFAULT_CLIENT_AI_QUOTA,
});

const defaultSetFeatureControls: Dispatch<SetStateAction<ClientFeatureControlsEntity>> = () => undefined;
const defaultSetAiQuota: Dispatch<SetStateAction<ClientAiQuotaSummaryEntity>> = () => undefined;

const ClientFeatureControlsContext = createContext<ClientFeatureControlsContextType>({
    featureControls: DEFAULT_CLIENT_FEATURE_CONTROLS,
    aiQuota: DEFAULT_CLIENT_AI_QUOTA,
    featureAvailability: DEFAULT_FEATURE_AVAILABILITY,
    setFeatureControls: defaultSetFeatureControls,
    setAiQuota: defaultSetAiQuota,
});

type ClientFeatureControlsProviderProps = PropsWithChildren & {
    initialFeatureControls: ClientFeatureControlsEntity;
    initialAiQuota?: ClientAiQuotaSummaryEntity;
};

export const useClientFeatureControls = (): ClientFeatureControlsContextType => {
    return useContext(ClientFeatureControlsContext);
};

export const ClientFeatureControlsProvider = ({
    children,
    initialFeatureControls,
    initialAiQuota,
}: ClientFeatureControlsProviderProps) => {
    const [featureControls, setFeatureControls] = useState<ClientFeatureControlsEntity>(() =>
        normalizeClientFeatureControlsEntity(initialFeatureControls),
    );
    const [aiQuota, setAiQuota] = useState<ClientAiQuotaSummaryEntity>(() =>
        normalizeClientAiQuotaSummaryEntity(initialAiQuota),
    );
    const featureAvailability = useMemo(
        () =>
            resolveClientAiQuotaFeatureAvailability({
                featureControls,
                quota: aiQuota,
            }),
        [featureControls, aiQuota],
    );

    const value = useMemo(
        () => ({
            featureControls,
            aiQuota,
            featureAvailability,
            setFeatureControls,
            setAiQuota,
        }),
        [featureControls, aiQuota, featureAvailability],
    );

    return <ClientFeatureControlsContext.Provider value={value}>{children}</ClientFeatureControlsContext.Provider>;
};
